import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
  cdcFullUpdateFields,
  cdcPartialUpdateFields,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const adaptivePlannerBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['adaptive-planner']),
});

export const planIdentifierSchema = z.object({
  id: z.number().int(),
});

const fieldChangeSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({ oldValue: schema, newValue: schema });

export const planChangesSchema = z.object({
  id: z.number().int(),
  name: fieldChangeSchema(z.string()).nullish(),
  schedulingValidations: fieldChangeSchema(z.array(z.string())).nullish(),
});

const planEntitySchema = planIdentifierSchema.loose();

export const scenarioIdentifierSchema = z.object({
  id: z.number().int(),
  loopId: z.number().int(),
});

export const scenarioStatusSchema = z.enum([
  'CREATED',
  'RESCHEDULE_QUEUED',
  'ML_INITIATED',
  'ML_VALIDATION_FAILED',
  'ML_VALIDATION_SUCCESS',
  'ML_RECEIVED',
  'TIMEOUT',
]);

const scenarioEntitySchema = scenarioIdentifierSchema
  .extend({
    status: scenarioStatusSchema,
    name: z.string(),
    organizationId: z.string(),
  })
  .loose();

export const planDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: planEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    ...cdcFullUpdateFields(planEntitySchema),
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    ...cdcPartialUpdateFields(planChangesSchema),
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: planIdentifierSchema,
  }),
]);

export const scenarioChangesSchema = z.object({
  id: z.number().int(),
  loopId: z.number().int(),
  status: fieldChangeSchema(z.string()).nullish(),
});

export const scenarioDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scenarioEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    ...cdcFullUpdateFields(scenarioEntitySchema),
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    ...cdcPartialUpdateFields(scenarioChangesSchema),
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.object({ id: z.number().int() }),
  }),
]);

export const scenarioOperationTypeSchema = z.enum([
  'SYNC',
  'RESET',
  'ACTIVATE',
  'GENERATE_AI_SUGGESTIONS',
]);

export const scenarioOperationStatusSchema = z.enum([
  'QUEUED',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'EXPIRED',
]);

export const scenarioOperationIdentifierSchema = z.object({
  id: z.number().int(),
  loopId: z.number().int(),
  scenarioId: z.number().int(),
});

// `requestedBy` is here to address the terminal notification, and must not reach the org-wide SSE
// payload built from this event.
const scenarioOperationEntitySchema = scenarioOperationIdentifierSchema
  .extend({
    organizationId: z.string(),
    type: scenarioOperationTypeSchema,
    status: scenarioOperationStatusSchema,
    requestedBy: z.string(),
  })
  .loose();

export const scenarioOperationDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-operation'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scenarioOperationEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-operation'),
    ...cdcFullUpdateFields(scenarioOperationEntitySchema),
  }),
]);

export const scenarioItemIdentifierSchema = z.object({
  scenarioId: z.number().int(),
  itemId: z.number().int(),
});

const scenarioItemEntitySchema = scenarioItemIdentifierSchema
  .extend({
    organizationId: z.string(),
    teamId: z.number().int().nullable(),
    estimationId: z.number().int().nullable(),
    name: z.string(),
    itemTypeId: z.number().int(),
    itemStatusId: z.number().int(),
    parentId: z.number().int().nullable(),
    externalKey: z.string().nullable(),
    earliestStartDateTime: z.string().nullable(),
    deadlineDateTime: z.string().nullable(),
    isDeletedInItemStore: z.boolean(),
    imputedFields: z.array(z.string()).nullable(),
    outOfScopeReason: z.string().nullable(),
    resourceIds: z.array(z.number().int()).nullable(),
  })
  .loose();

export const scenarioItemDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-item'),
    ...cdcFullUpdateFields(scenarioItemEntitySchema),
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-item'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: scenarioItemIdentifierSchema,
  }),
]);

export const scheduleIdentifierSchema = z.object({
  scenarioId: z.number().int(),
  itemId: z.number().int(),
});

const scheduleEntitySchema = scheduleIdentifierSchema
  .extend({
    organizationId: z.string(),
    forecastedStartDate: z.string(),
    forecastedFinishDate: z.string(),
    latestFinishDate: z.string(),
    source: z.enum(['LOOP', 'MCP']).default('LOOP'),
  })
  .loose();

export const scheduleDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    ...cdcFullUpdateFields(scheduleEntitySchema),
  }),
]);

export const planAlertTypeSchema = z.enum([
  'PLAN_SCOPE_CHANGE',
  'PLAN_ITEM_EDITED',
  'ITEM_STORE_SCHEDULE_CHANGES',
]);

export const planAlertIdentifierSchema = z.object({
  id: z.number().int(),
  planId: z.number().int(),
  alertType: planAlertTypeSchema,
});

export const planAlertEntitySchema = planAlertIdentifierSchema.extend({
  createdAt: z.string(),
});

export const planAlertDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan-alert'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: planAlertEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('plan-alert'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: planAlertIdentifierSchema,
  }),
]);

export const scenarioAlertTypeSchema = z.enum([
  'SCENARIO_ITEM_MODIFIED',
  'ITEM_STORE_SCHEDULE_CHANGES',
  'ITEM_STORE_DEPENDENCY_CHANGES',
  'ITEM_STORE_ITEMS_ADDED',
  'ITEM_STORE_ITEMS_DELETED',
  'TEAM_COMPOSITION_UPDATED',
  'ESTIMATION_EFFORT_UPDATED',
  'ITEMS_UNDER_DONE_ANCESTOR',
  'TEAM_WITHOUT_MEMBERS',
  'PENDING_RESCHEDULE',
]);

export const scenarioAlertIdentifierSchema = z.object({
  id: z.number().int(),
});

export const scenarioAlertEntitySchema = scenarioAlertIdentifierSchema.extend({
  scenarioId: z.number().int(),
  alertType: scenarioAlertTypeSchema,
  createdAt: z.string(),
  createdBy: z.string(),
});

export const scenarioAlertDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-alert'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: scenarioAlertEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-alert'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: scenarioAlertIdentifierSchema,
  }),
]);

export const loopScheduleAlertTypeSchema = z.enum(['PENDING_RESCHEDULE']);

export const loopScheduleAlertIdentifierSchema = z.object({
  id: z.number().int(),
});

export const loopScheduleAlertEntitySchema = loopScheduleAlertIdentifierSchema.extend({
  loopId: z.number().int(),
  alertType: loopScheduleAlertTypeSchema,
  createdAt: z.string(),
  createdBy: z.string(),
});

export const loopScheduleAlertDataChangeDTOSchema = discriminatedUnion('eventType', [
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('loop-schedule-alert'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: loopScheduleAlertEntitySchema,
  }),
  adaptivePlannerBaseCdcEventSchema.extend({
    entityType: z.literal('loop-schedule-alert'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: loopScheduleAlertIdentifierSchema,
  }),
]);

const adaptivePlannerEntityDataChangeDTOSchemas = [
  planDataChangeDTOSchema,
  scenarioDataChangeDTOSchema,
  planAlertDataChangeDTOSchema,
  scenarioAlertDataChangeDTOSchema,
  loopScheduleAlertDataChangeDTOSchema,
  scenarioItemDataChangeDTOSchema,
  scenarioOperationDataChangeDTOSchema,
  scheduleDataChangeDTOSchema,
] as const;

export const adaptivePlannerEntityTypes: string[] = adaptivePlannerEntityDataChangeDTOSchemas.map(
  (schema) => schema.options[0].shape.entityType.value,
);

export const adaptivePlannerDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(
    discriminatedUnion('entityType', adaptivePlannerEntityDataChangeDTOSchemas),
  ),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type AdaptivePlannerDataChangeDTO = z.output<typeof adaptivePlannerDataChangeDTOSchema>;
export type PlanDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'plan' }
>;
export type ScenarioDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'scenario' }
>;
export type PlanAlertDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'plan-alert' }
>;
export type ScenarioItemDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'scenario-item' }
>;
export type ScheduleDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'schedule' }
>;
export type ScenarioOperationDataChangeDTO = Extract<
  AdaptivePlannerDataChangeDTO['data'],
  { entityType: 'scenario-operation' }
>;
